import { UseQueryOptions, UseQueryResult, useQuery } from "@tanstack/react-query";

import { useAuth } from "@/dashboard/contexts/auth-context";

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

/**
 * Core prefix for every tenant-scoped key.
 *   ["tenant", "<id>", …rest]
 */
export type TenantKeyPrefix = readonly ["tenant", string];

/**
 * Helper that turns an arbitrary key tail into a tenant-aware key.
 */
export type TenantKey<K extends readonly unknown[]> = readonly [...TenantKeyPrefix, ...K];

/**
 * Type for the closure returned by useTenantKeyBuilder.
 *
 * @example
 * const tk = useTenantKeyBuilder(); // ⇒ (...parts) => ["tenant", id, …parts]
 * queryClient.invalidateQueries(tk("business-units", jobTypeId ?? null));
 */
type TenantKeyBuilder = <K extends readonly unknown[]>(...parts: K) => TenantKey<K>;

/* ------------------------------------------------------------------ */
/* Key builders                                                        */
/* ------------------------------------------------------------------ */

/**
 * Build a tenant key when you **already have** the tenantId (e.g. in
 * server-side utilities or background jobs).
 *
 * @example
 * const key = tenantKey("abc-123", "business-units", jobTypeId ?? null);
 */
export function tenantKey<const K extends readonly unknown[]>(
  tenantId: string,
  ...parts: K
): TenantKey<K> {
  return ["tenant", tenantId, ...parts];
}

/**
 * Hook that returns a closure for building tenant keys **inside React**,
 * automatically injecting the active tenantId from context.
 *
 * @throws if called before the tenant has resolved (maybe unsafe, we should revisit).
 *
 * @example
 * const tk = useTenantKey(); // ⇒ (...parts) => ["tenant", id, …parts]
 * queryClient.invalidateQueries(tk("business-units", jobTypeId ?? null));
 */
function useTenantKeyBuilder(): TenantKeyBuilder {
  const { activeTenantId } = useAuth();

  if (!activeTenantId) {
    throw new Error("No tenant ID found in useTenantKeyBuilder");
  }

  return (<K extends readonly unknown[]>(...parts: K) =>
    tenantKey(activeTenantId, ...parts)) as TenantKeyBuilder;
}

/**
 * Like `useTenantKeyBuilder`, but takes an *entire* tuple key instead of
 * individual parts, so callers don't need the spread operator.
 *
 *     const tkTuple = useTenantKeyFromTuple();
 *     const key     = tkTuple(groupsQueryKey());   // ["tenant", id, "groups"]
 */
export function useTenantKeyFromTuple() {
  const buildKey = useTenantKeyBuilder();
  return <K extends readonly unknown[]>(tuple: K) => buildKey(...tuple);
}

/* ------------------------------------------------------------------ */
/* React-Query helper wrappers                                        */
/* ------------------------------------------------------------------ */

/**
 * `useTenantQuery`
 *
 * Wrapper around `useQuery` that:
 *   • prefixes the key with ["tenant", activeTenantId, …]
 *   • auto-gates until tenantId is resolved
 *
 * EXAMPLE – basic data fetch
 * -----------------------------------------------------------
 * const { data, isLoading } = useTenantQuery({
 *   queryKey: ["contacts"],
 *   queryFn: fetchContacts,
 *   staleTime: 5 * 60_000,
 * });
 *
 * //  => Actual key in cache:
 * //     ["tenant", "abc-123", "contacts"]
 */
export function useTenantQuery<
  TQueryFnData,
  TError = Error,
  TData = TQueryFnData,
  K extends readonly unknown[] = readonly unknown[],
>(
  opts: Omit<UseQueryOptions<TQueryFnData, TError, TData, TenantKey<K>>, "queryKey"> & {
    queryKey: K;
  }
): UseQueryResult<TData, TError> {
  const { activeTenantId } = useAuth();
  const enabled = !!activeTenantId && (opts.enabled ?? true);
  return useQuery({
    ...opts,
    enabled,
    queryKey: tenantKey(activeTenantId as string, ...opts.queryKey),
  });
}
